// @owners { team: patients }
import { Toast, ToastContext } from '@alto/design-system';
import {
  CartEndpoint,
  type CartEndpointFetchActiveResponse,
  type CartEndpointRemoveItemRequest,
} from '@alto/scriptdash/alto/patient_app/carts/v1/cart_endpoint';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useContext, useState } from 'react';
import {
  type RemoveFromCartPayload,
  clearDeliveryWindows,
  fetchWindows,
  removeFromCart__DEPRECATED,
  resetOrdersByIndex,
  updateOrder,
} from '~shared/actions/checkout';
// eslint-disable-next-line import/no-deprecated
import { fetchNextAvailableDates } from '~shared/actions/nextAvailableDates';
import { useCartData } from '~shared/features/cart/hooks/useCartData';
import { usePrescriptionsInCart } from '~shared/features/cart/hooks/usePrescriptionsInCart';
import { getIsOrderBundling } from '~shared/features/checkout/selectors/getCheckoutFlow';
import { getOrderIncludeSharpsContainer } from '~shared/features/checkout/selectors/getOrder';
import { getNextDeliveryByPrescriptionID } from '~shared/features/delivery/selectors/getNextDeliveryForPrescriptionID';
import getDeliveriesForShipmentID from '~shared/features/delivery-info/selectors/getDeliveriesForShipmentID';
import { deleteAddOnPrescription } from '~shared/features/essentials/actions';
import { useNextAvailableDateAddressId } from '~shared/features/next-available-date/hooks/useNextAvailableDateAddressId';
import { getNextAvailableDatesByPrescriptionID } from '~shared/features/next-available-date/selectors/getNextAvailableDatesByPrescriptionID';
import getShipmentID from '~shared/features/next-available-date/selectors/getShipmentId';
import { getPrescriptionsHash } from '~shared/features/prescriptions/selectors/getPrescriptionByID';
import { apiEndpointHandler } from '~shared/helpers/api';
import { getErrorMessageFromResponse, isControl, isExpensive } from '~shared/helpers/helper';
import { useAnalytics, useIsInCheckout } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useCartItemsRemovedEvent } from '~shared/lib/analytics/src/hooks/useCartItemRemovedEvent';
import { useOrderPricing } from '~shared/queries';
import { queries } from '~shared/queries/query-keys';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { useCheckoutAddressInfoKeys } from '../../checkout';

const cartEndpoint = CartEndpoint(apiEndpointHandler);

type RemoveFromCartParams = {
  item: RemoveFromCartPayload;
  // this is used for analytic events. see example in cross sell container
  // removeFromCart: (prescription: Prescription, index: number)
  // dispatch(sendAnalyticsEvent(createEvent(EVENTS.REFILL_CROSS_SELL_ITEM_REMOVED, { prescription, index }),
  properties?: Record<string, any> | null;
};

type RemoveFromCartWithCreatePayload = RemoveFromCartParams & {
  payload: CartEndpointRemoveItemRequest;
};

type RemoveFromCartResult = {
  success: boolean;
};

type UseRemoveFromCart = {
  isPending: boolean;
  isSuccess: boolean;
  handleRemoveFromCart: (params: RemoveFromCartParams) => Promise<RemoveFromCartResult>;
};

/**
 * Hook used to remove an item from the patient's cart.
 *
 * // export the errorMessage to render in your component
 * const { handleRemoveFromCart } = useRemoveFromCart();
 *
 * const result = await handleRemoveFromCart({ item, onSuccess: onRemoveFromCartSuccess });
 *
 * // perform logic after the item is successfully removed from cart
 * if (result.success) {
 *   ...
 * }
 */

export const useRemoveFromCart = (): UseRemoveFromCart => {
  const { trackEvent } = useAnalytics();
  const dispatch = useDispatchShared();
  const queryClient = useQueryClient();
  const { cartItems } = useCartData();
  const { injectables } = usePrescriptionsInCart();
  const { refetchOrderPricing } = useOrderPricing();
  const prescriptions = useSelectorShared(getPrescriptionsHash);
  const { prescriptions: cartPrescriptions } = usePrescriptionsInCart();
  const { keys: addressInfoKeys, homeToSignConfirmationRequired } = useCheckoutAddressInfoKeys();
  const includeSharpsContainer = useSelectorShared(getOrderIncludeSharpsContainer);
  const nextDeliveryByPrescriptionID = useSelectorShared(getNextDeliveryByPrescriptionID);
  const [isSuccess, setIsSuccess] = useState(false);
  const { addToast } = useContext(ToastContext);
  const isOrderBundling = useSelectorShared(getIsOrderBundling);
  const { cartItemsRemovedEvent, cartItemsRemovedFailedEvent } = useCartItemsRemovedEvent();
  const nextAvailableDateAddressID = useNextAvailableDateAddressId();
  const nextAvailableDatesByPrescriptionId = useSelectorShared((state) =>
    getNextAvailableDatesByPrescriptionID(state, { nextAvailableDateAddressID }),
  );
  const shipmentID = useSelectorShared(getShipmentID);
  const deliveriesForShipment = useSelectorShared((state) => getDeliveriesForShipmentID(state, { shipmentID }));
  const isInCheckout = useIsInCheckout();
  const resetHomeToSignConfirmation = (prescriptionID: number) => {
    // Don't reset home_to_sign_confirmation because the bundled order likely requires it
    if (!isOrderBundling) {
      const remainingCartPrescriptions = cartPrescriptions.filter((rx) => rx.id !== prescriptionID);
      const hasControlInCart = remainingCartPrescriptions.some(isControl);
      const isExpensiveCart = isExpensive(remainingCartPrescriptions);

      // reset home to sign confirmation if the order no longer contains expensive meds and there are no controlled meds
      if (addressInfoKeys.expensive && !isExpensiveCart && !hasControlInCart) {
        dispatch(updateOrder({ home_to_sign_confirmation: undefined }));
      }

      // reset home to sign confirmation when no longer required
      if (homeToSignConfirmationRequired && !isExpensiveCart && !hasControlInCart) {
        dispatch(updateOrder({ home_to_sign_confirmation: undefined }));
      }
    }
  };

  const clearIncludeSharpsContainer = useCallback(
    (prescriptionID: number) => {
      const includeSharpsContainerSet = includeSharpsContainer !== undefined;
      const remainingInjectables = injectables.filter((injectable) => injectable.resource_id !== prescriptionID);

      if (remainingInjectables.length === 0 && includeSharpsContainerSet) {
        dispatch(updateOrder({ include_sharps_container: undefined }));
      }
    },
    [dispatch, includeSharpsContainer, injectables],
  );

  const removeAddOnPrescriptionAndRefreshData = useCallback(
    (item: RemoveFromCartPayload) => {
      const prescription = prescriptions[item.resource_id];
      const remainingPrescriptionIDs = cartPrescriptions.filter((rx) => rx.id !== item.resource_id).map((rx) => rx.id);

      void dispatch(
        // eslint-disable-next-line import/no-deprecated
        fetchNextAvailableDates({
          unbundledPrescriptionIDs: remainingPrescriptionIDs,
          bundledPrescriptionIDs: remainingPrescriptionIDs,
          addressID: nextAvailableDateAddressID,
        }),
      ).catch(() => {});
      resetHomeToSignConfirmation(item.resource_id);

      if (item.resource_type === 'Prescription' && prescription?.is_add_on_otc) {
        // Fire-and-forget: backend garbage-collect only; Promise.resolve handles mocks/non-Promise returns in tests.
        void Promise.resolve(deleteAddOnPrescription(prescription?.id)).catch((e: unknown) => {
          console.warn(e);
        });
        trackEvent({
          event: EVENTS.ESSENTIALS__OTC_REMOVED_FROM_CART,
          params: {
            patient_id: prescription.user_id,
            sku: prescription.sku,
            sku_type: prescription.sku_type,
            prescription_id: prescription.id,
            delivery_id: nextDeliveryByPrescriptionID[prescription.id]?.id,
          },
          additionalFields: {
            deliveryId: nextDeliveryByPrescriptionID[prescription.id]?.id,
            prescriptionId: prescription.id,
          },
        });
      }

      clearIncludeSharpsContainer(item.resource_id);

      if (cartItems.length === 0 || (cartItems.length === 1 && cartItems[0].resource_id === item.resource_id)) {
        dispatch(clearDeliveryWindows());
        dispatch(
          updateOrder({
            tip_amount: undefined,
            deliver_before: undefined,
            deliver_after: undefined,
            date: undefined,
            delivery_method: undefined,
            pay_at_pickup: undefined,
            pickup_address: undefined,
            pickup_ready_time: undefined,
            is_asap: undefined,
            pay_with_apple_pay: undefined,
            payment_method_id: undefined,
            tip_payment_method_id: undefined,
          }),
        );
      } else {
        dispatch(updateOrder({ date: undefined }));
      }

      void Promise.resolve(refetchOrderPricing()).catch(() => {});

      const remainingPrescriptionIds = cartItems
        .filter((cartItem) => cartItem.resource_id !== item.resource_id)
        .map((cartItem) => cartItem.resource_id);

      void Promise.resolve(dispatch(fetchWindows(remainingPrescriptionIds))).catch(() => {});
      // reset ordersByIndex and orderIndex for split shipments when removing items from cart
      // because the compound medication could be removed
      dispatch(resetOrdersByIndex());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, cartItems, prescriptions, trackEvent, clearIncludeSharpsContainer],
  );

  const { isPending, mutateAsync } = useMutation({
    mutationFn: async (params: RemoveFromCartWithCreatePayload) => {
      setIsSuccess(false);
      const response = await cartEndpoint.removeItem(params.payload);
      return { response, params };
    },
    onMutate: (params: RemoveFromCartWithCreatePayload) => {
      // optimistically remove the item from the cart before the mutation starts
      const previousState = queryClient.getQueryData<CartEndpointFetchActiveResponse>(queries.cart.active({}).queryKey);
      const items = previousState?.data?.data?.items || [];
      queryClient.setQueryData(queries.cart.active({}).queryKey, {
        data: {
          ...previousState?.data,
          data: {
            ...previousState?.data?.data,
            items: items.filter((item) => {
              return !(
                item.resource_type === params.item.resource_type && item.resource_id === params.item.resource_id
              );
            }),
          },
        },
      });
      return { previousState };
    },
    onError: (_error, _params, context) => {
      // restore the previous state if the mutation fails
      queryClient.setQueryData(queries.cart.active({}).queryKey, context?.previousState);
      // refetch to reconcile any stale cart item IDs (e.g. item deleted server-side)
      queryClient.invalidateQueries({ queryKey: queries.cart.active({}).queryKey });
    },
  });

  const removeFromCart = useCallback(
    async ({ item, properties }: RemoveFromCartParams) => {
      try {
        const prescription = prescriptions[item.resource_id];
        if (!prescription?.user_id || !prescription?.medication_name) {
          cartItemsRemovedFailedEvent({
            removedPrescriptionIds: [item.resource_id],
            shipmentId: shipmentID,
            deliveriesForShipment,
            error: 'missing user id or medication name for remove from cart',
          });
          throw new Error('missing user id or medication name for remove from cart');
        }

        const cartItem = cartItems.find(
          (cartItem) => cartItem.resource_type === item.resource_type && cartItem.resource_id === item.resource_id,
        );

        if (!cartItem?.id || (!isInCheckout && cartItem.id === -1)) {
          throw new Error('Unable to remove item from cart. Please try again.');
        }
        if (isInCheckout) {
          dispatch(removeFromCart__DEPRECATED(item, properties));

          if (cartItem?.id === -1) {
            cartItemsRemovedEvent({
              removedPrescriptionIds: [item.resource_id],
              allPrescriptionsInCart: cartPrescriptions,
              nextAvailableDatesByPrescriptionId,
              shipmentId: shipmentID,
              deliveriesForShipment,
              inCheckout: true,
            });
            return { success: true };
          }
        }
        const payload: CartEndpointRemoveItemRequest = { id: cartItem.id };
        const { response } = await mutateAsync({ item, payload, properties });
        if (response.errors?.length) {
          const errorMessage = getErrorMessageFromResponse({ errors: response.errors });
          addToast(<Toast variant="error">{errorMessage}</Toast>);
          cartItemsRemovedFailedEvent({
            removedPrescriptionIds: [item.resource_id],
            shipmentId: shipmentID,
            deliveriesForShipment,
            error: 'api error',
          });
          return { success: false };
        }

        queryClient.setQueryData(queries.cart.active({}).queryKey, () => ({ data: response }));

        // trigger the cart middleware
        removeAddOnPrescriptionAndRefreshData(item);
        cartItemsRemovedEvent({
          removedPrescriptionIds: [item.resource_id],
          allPrescriptionsInCart: cartPrescriptions,
          nextAvailableDatesByPrescriptionId,
          shipmentId: shipmentID,
          deliveriesForShipment,
          inCheckout: false,
        });
        return { success: true };
      } catch (e) {
        console.warn(e);
        const errorMessage = getErrorMessageFromResponse({});
        addToast(<Toast variant="error">{errorMessage}</Toast>);
        cartItemsRemovedFailedEvent({
          removedPrescriptionIds: [item.resource_id],
          shipmentId: shipmentID,
          deliveriesForShipment,
          error: 'other error',
        });
        return { success: false };
      }
    },
    [
      prescriptions,
      cartItemsRemovedFailedEvent,
      shipmentID,
      deliveriesForShipment,
      mutateAsync,
      queryClient,
      removeAddOnPrescriptionAndRefreshData,
      cartItemsRemovedEvent,
      cartPrescriptions,
      nextAvailableDatesByPrescriptionId,
      addToast,
      cartItems,
      isInCheckout,
      dispatch,
    ],
  );

  return {
    isSuccess,
    isPending,
    handleRemoveFromCart: removeFromCart,
  };
};
